import { styled } from "@mui/material/styles";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckIcon } from "./icons";
import { focusRing, reducedMotion } from "./tokens";
import { usePresence } from "./usePresence";
const POPOVER_MS = 180;
const OFFSET = 6;
const EDGE = 8;
const PopoverRoot = styled("div", {
    shouldForwardProp: (prop)=>prop !== "isVisible"
})(({ isVisible })=>({
        position: "fixed",
        zIndex: 1300,
        maxWidth: `calc(100vw - ${EDGE * 2}px)`,
        overflowY: "auto",
        background: "var(--j-surface)",
        color: "var(--j-ink)",
        borderRadius: "var(--j-r-lg)",
        boxShadow: "var(--j-e2)",
        padding: "var(--j-sp-1)",
        outline: "none",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : "scale(.96)",
        transition: `opacity ${POPOVER_MS}ms var(--j-ease), transform ${POPOVER_MS}ms var(--j-ease)`,
        [reducedMotion]: {
            transform: "none"
        }
    }));
function computePosition(anchor, placement) {
    const rect = anchor.getBoundingClientRect();
    const isTop = placement.startsWith("top");
    const isEnd = placement.endsWith("end");
    const half = rect.width / 2;
    const style = {
        transformOrigin: `${isEnd ? `calc(100% - ${half}px)` : `${half}px`} ${isTop ? "bottom" : "top"}`
    };
    if (isTop) {
        style.bottom = window.innerHeight - rect.top + OFFSET;
        style.maxHeight = rect.top - OFFSET - EDGE;
    } else {
        style.top = rect.bottom + OFFSET;
        style.maxHeight = window.innerHeight - rect.bottom - OFFSET - EDGE;
    }
    if (isEnd) style.right = Math.max(EDGE, window.innerWidth - rect.right);
    else style.left = Math.max(EDGE, rect.left);
    return {
        style
    };
}
const ITEM_SELECTOR = '[role^="menuitem"]:not([disabled]), [role="option"]:not([aria-disabled="true"])';
export function Popover({ open, anchorEl, onClose, placement = "bottom-end", width = 248, role = "menu", "aria-label": ariaLabel, children, className }) {
    const { isMounted, isVisible } = usePresence(open, POPOVER_MS);
    const rootRef = useRef(null);
    const [position, setPosition] = useState(null);
    useLayoutEffect(()=>{
        if (!open || !anchorEl) return undefined;
        const update = ()=>setPosition(computePosition(anchorEl, placement));
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return ()=>{
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [
        open,
        anchorEl,
        placement
    ]);
    useEffect(()=>{
        if (!open) return undefined;
        const handlePointerDown = (event)=>{
            const target = event.target;
            if (rootRef.current?.contains(target) || anchorEl?.contains(target)) return;
            onClose();
        };
        const handleKeyDown = (event)=>{
            if (event.key !== "Escape") return;
            event.stopPropagation();
            onClose();
        };
        document.addEventListener("mousedown", handlePointerDown, true);
        document.addEventListener("touchstart", handlePointerDown, true);
        document.addEventListener("keydown", handleKeyDown, true);
        return ()=>{
            document.removeEventListener("mousedown", handlePointerDown, true);
            document.removeEventListener("touchstart", handlePointerDown, true);
            document.removeEventListener("keydown", handleKeyDown, true);
        };
    }, [
        open,
        anchorEl,
        onClose
    ]);
    useEffect(()=>{
        if (!open) return undefined;
        const frame = requestAnimationFrame(()=>{
            const first = rootRef.current?.querySelector("input:not([disabled])") ?? rootRef.current?.querySelector(ITEM_SELECTOR);
            (first ?? rootRef.current)?.focus();
        });
        return ()=>{
            cancelAnimationFrame(frame);
            const active = document.activeElement;
            if (!active || active === document.body || rootRef.current?.contains(active)) anchorEl?.focus();
        };
    }, [
        open,
        anchorEl
    ]);
    const handleKeyDown = (event)=>{
        const input = rootRef.current?.querySelector("input:not([disabled])");
        const items = Array.from(rootRef.current?.querySelectorAll(ITEM_SELECTOR) ?? []);
        if (event.key === "Tab") {
            if (!input) {
                onClose();
                return;
            }
            event.preventDefault();
            const target = event.target === input ? items.find((item)=>item.matches("[aria-selected=true]")) ?? items[0] : input;
            target?.focus();
            return;
        }
        if (!items.length) return;
        const isInInput = event.target instanceof HTMLInputElement;
        if (isInInput && event.key !== "ArrowDown") return;
        const index = items.indexOf(document.activeElement);
        const moves = {
            ArrowDown: index + 1,
            ArrowUp: index < 0 ? items.length - 1 : index - 1,
            Home: 0,
            End: items.length - 1
        };
        if (!(event.key in moves)) return;
        event.preventDefault();
        items[(moves[event.key] + items.length) % items.length].focus();
    };
    if (!isMounted || !position) return null;
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(PopoverRoot, {
        ref: rootRef,
        isVisible: isVisible && open,
        role: role,
        "aria-label": ariaLabel,
        tabIndex: -1,
        className: className,
        style: {
            ...position.style,
            width
        },
        onKeyDown: handleKeyDown
    }, children), document.body);
}
const ItemRoot = styled("button")({
    ...focusRing,
    width: "100%",
    minHeight: "var(--j-btn-h)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: "0 var(--j-sp-2)",
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    color: "var(--j-ink)",
    fontSize: "var(--j-fs-md)",
    lineHeight: "var(--j-lh-md)",
    textAlign: "left",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:focus-visible": {
        outlineOffset: "var(--j-focus-inset-offset)"
    },
    "&:hover:not(:disabled)": {
        background: "var(--j-hover)"
    },
    "& > svg": {
        color: "var(--j-ink-2)"
    },
    "&[aria-checked=true]": {
        background: "var(--j-accent-soft)",
        color: "var(--j-accent-ink)",
        "& > svg": {
            color: "var(--j-accent-ink)"
        }
    },
    "&[data-tone=muted]": {
        color: "var(--j-ink-2)"
    },
    "&[data-tone=danger], &[data-tone=danger] > svg": {
        color: "var(--j-err)"
    },
    "&:disabled, &:disabled > svg": {
        color: "var(--j-ink-3)",
        cursor: "default"
    },
    "& > .menu-label": {
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    "& > svg, & > .menu-icon": {
        flexShrink: 0
    }
});
export function MenuItem({ icon, children, selected, isCheckbox, trailing, tone = "default", onClick, disabled, className }) {
    const isRadio = selected !== undefined;
    return /*#__PURE__*/ React.createElement(ItemRoot, {
        type: "button",
        role: isRadio ? isCheckbox ? "menuitemcheckbox" : "menuitemradio" : "menuitem",
        "aria-checked": isRadio ? selected : undefined,
        "data-tone": tone,
        tabIndex: -1,
        disabled: disabled,
        onClick: onClick,
        className: className
    }, icon, /*#__PURE__*/ React.createElement("span", {
        className: "menu-label"
    }, children), trailing, selected && /*#__PURE__*/ React.createElement(CheckIcon, {
        size: 14
    }));
}
const Separator = styled("div")({
    height: 1,
    background: "var(--j-line)",
    margin: "var(--j-sp-1) 0"
});
export function MenuSeparator() {
    return /*#__PURE__*/ React.createElement(Separator, {
        role: "separator"
    });
}
const Label = styled("div")({
    fontSize: "var(--j-fs-xs)",
    lineHeight: "var(--j-lh-xs)",
    fontWeight: 600,
    color: "var(--j-ink-3)",
    padding: "var(--j-sp-1-5) var(--j-sp-2) var(--j-sp-0-5)"
});
export function MenuLabel({ children, id }) {
    return /*#__PURE__*/ React.createElement(Label, {
        role: "presentation",
        id: id
    }, children);
}
